import React from 'react';
import { Plus, Bot, Loader, Check } from 'lucide-react';
import { TeamSelector } from '../../components/TeamSelector';
import { UserProfile } from '../../components/UserProfile';
import { useWindows } from '../../contexts/WindowsContext';
import { useTaskCapture } from '../../hooks/useTaskCapture';
import { User, TeamWithMembers } from '../../types';

interface AppHeaderProps {
  user: User | null;
  selectedTeamId: string | null;
  onTeamSelect: (teamId: string | null) => void;
  onCreateTeam: () => void;
  onJoinTeam: () => void;
  onTeamSettings: (team: TeamWithMembers) => void;
  onShowAIAssistant: () => void;
  onShowProcessInbox: () => void;
  inboxCount: number;
  onShowTodoForm: () => void;
}

export function AppHeader({
  user,
  selectedTeamId,
  onTeamSelect,
  onCreateTeam,
  onJoinTeam,
  onTeamSettings,
  onShowAIAssistant,
  onShowProcessInbox,
  inboxCount,
  onShowTodoForm
}: AppHeaderProps) {
  const { homeWindowId, setActiveWindow } = useWindows();
  const capture = useTaskCapture('quick-inbox');

  return (
    <header className="bg-white dark:bg-gray-900 shadow-lg border-b border-gray-200 dark:border-gray-700 backdrop-blur-sm bg-white/95 dark:bg-gray-900/95 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row justify-between items-center py-4 sm:py-6 space-y-4 sm:space-y-0">
          <div className="flex items-center space-x-6">
            <h1
              onClick={() => homeWindowId && setActiveWindow(homeWindowId)}
              className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent cursor-pointer hover:opacity-80 transition-opacity"
              title="Go to home screen"
            >
              Todo Manager
            </h1>
            <TeamSelector
              selectedTeamId={selectedTeamId}
              onTeamSelect={onTeamSelect}
              onCreateTeam={onCreateTeam}
              onJoinTeam={onJoinTeam}
              onTeamSettings={onTeamSettings}
            />
          </div>

          <div className="flex items-center space-x-2 sm:space-x-4">
            <UserProfile selectedTeamId={selectedTeamId} />

            {user?.preferences?.aiFeaturesEnabled === true && (
              <button
                onClick={onShowAIAssistant}
                className="btn-secondary flex items-center space-x-1 sm:space-x-2 group text-sm sm:text-base px-3 sm:px-6 py-2 sm:py-3"
                aria-label="Open AI Assistant"
              >
                <Bot className="w-4 h-4 sm:w-5 sm:h-5 group-hover:scale-110 transition-transform duration-300" />
                <span className="hidden sm:inline">AI Assistant</span>
              </button>
            )}

            {/* Inbox: combined text input + open button */}
            <div className="relative">
              <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden bg-white dark:bg-gray-800">
                <div className="relative flex items-center">
                  <input
                    type="text"
                    value={capture.input}
                    onChange={e => capture.setInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') void capture.submit(); }}
                    placeholder="Add to inbox..."
                    disabled={capture.isLoading}
                    className="px-3 py-2 text-sm bg-transparent outline-none text-gray-700 dark:text-gray-300 placeholder-gray-400 w-36 sm:w-44 disabled:opacity-50"
                  />
                  {capture.isLoading && (
                    <Loader className="w-3.5 h-3.5 text-blue-500 animate-spin absolute right-2" />
                  )}
                </div>
                <button
                  onClick={onShowProcessInbox}
                  className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 border-l border-gray-300 dark:border-gray-600 flex items-center gap-1.5 whitespace-nowrap transition-colors"
                  aria-label="Process Inbox"
                >
                  <span>Open inbox</span>
                  {inboxCount > 0 && (
                    <span className="text-[11px] text-red-400/70 font-medium">{inboxCount}</span>
                  )}
                </button>
              </div>
              {(capture.progress || capture.receipt || capture.reply || capture.error) && (
                <div className="absolute top-full left-0 mt-2 z-50 w-80 max-w-[80vw] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg px-3 py-2 space-y-1 text-sm text-gray-700 dark:text-gray-300">
                  {capture.progress && <p role="status">{capture.progress}</p>}
                  {capture.receipt && <p className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-1" />{capture.receipt}</p>}
                  {capture.reply && <p>{capture.reply}</p>}
                  {capture.error && <p role="alert" className="text-red-600 dark:text-red-400">{capture.error}</p>}
                  <button type="button" onClick={capture.dismiss} disabled={capture.isLoading}
                    className="text-xs underline disabled:opacity-50" aria-label="Dismiss capture feedback">Dismiss</button>
                </div>
              )}
            </div>

            <button
              onClick={onShowTodoForm}
              className="btn-primary flex items-center space-x-1 sm:space-x-2 group text-sm sm:text-base px-3 sm:px-6 py-2 sm:py-3"
              aria-label="Add new task"
            >
              <Plus className="w-4 h-4 sm:w-5 sm:h-5 group-hover:rotate-90 transition-transform duration-300" />
              <span className="hidden sm:inline">Add Task</span>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}
